import { ref, onMounted } from 'vue'
import {
  projectsApi,
  peopleApi,
  tagsApi,
  tagGroupsApi,
  type TagItem,
  type TagGroupItem,
} from '@/plugins/api'
import type { LookupsInvalidateEvent } from './useNotifications'

export interface LookupProject {
  id: string
  title: string
}

export interface LookupPerson {
  id: string
  displayName: string
}

// Module-level shared state — same data for all components
const projects = ref<LookupProject[]>([])
const people = ref<LookupPerson[]>([])
const tags = ref<TagItem[]>([])
const tagGroups = ref<TagGroupItem[]>([])
const tagColorMap = ref<Map<string, string>>(new Map())
const projectsLoaded = ref(false)
const peopleLoaded = ref(false)
const tagsLoaded = ref(false)
const projectsLoading = ref(false)
const peopleLoading = ref(false)
const tagsLoading = ref(false)

// Debounce timers
let projectsDebounceTimer: ReturnType<typeof setTimeout> | null = null
let peopleDebounceTimer: ReturnType<typeof setTimeout> | null = null

const DEBOUNCE_MS = 500

async function loadProjects() {
  if (projectsLoading.value) return
  projectsLoading.value = true
  try {
    const response = await projectsApi.getAll({ limit: 100, status: 'active' })
    projects.value = response.data.data.map((p: { id: string; title: string }) => ({
      id: p.id,
      title: p.title,
    }))
    projectsLoaded.value = true
  } catch (error) {
    console.error('[useLookups] Failed to load projects:', error)
  } finally {
    projectsLoading.value = false
  }
}

async function loadPeople() {
  if (peopleLoading.value) return
  peopleLoading.value = true
  try {
    const response = await peopleApi.getAll({ limit: 200, isActive: true })
    const items = response.data.data || response.data.items || []
    people.value = items.map((p: { id: string; fullName?: string; shortName?: string }) => ({
      id: p.id,
      displayName: p.fullName || p.shortName || 'Неизвестный',
    }))
    peopleLoaded.value = true
  } catch (error) {
    console.error('[useLookups] Failed to load people:', error)
  } finally {
    peopleLoading.value = false
  }
}

async function loadTags() {
  if (tagsLoading.value) return
  tagsLoading.value = true
  try {
    // Группы тегов — часть справочника: грузятся и инвалидируются вместе с тегами
    const [response, groupsResponse] = await Promise.all([
      tagsApi.getAll(),
      tagGroupsApi.getAll().catch((error) => {
        console.error('[useLookups] Failed to load tag groups:', error)
        return { data: tagGroups.value }
      }),
    ])
    tagGroups.value = groupsResponse.data || []
    tags.value = response.data || []
    const map = new Map<string, string>()
    for (const tag of tags.value) {
      map.set(tag.name.toLowerCase(), tag.color)
    }
    tagColorMap.value = map
    tagsLoaded.value = true
  } catch (error) {
    console.error('[useLookups] Failed to load tags:', error)
  } finally {
    tagsLoading.value = false
  }
}

function debouncedLoadProjects() {
  if (projectsDebounceTimer) clearTimeout(projectsDebounceTimer)
  projectsDebounceTimer = setTimeout(() => {
    loadProjects()
  }, DEBOUNCE_MS)
}

function debouncedLoadPeople() {
  if (peopleDebounceTimer) clearTimeout(peopleDebounceTimer)
  peopleDebounceTimer = setTimeout(() => {
    loadPeople()
  }, DEBOUNCE_MS)
}

let tagsDebounceTimer: ReturnType<typeof setTimeout> | null = null

function debouncedLoadTags() {
  if (tagsDebounceTimer) clearTimeout(tagsDebounceTimer)
  tagsDebounceTimer = setTimeout(() => {
    loadTags()
  }, DEBOUNCE_MS)
}

// WebSocket subscription — set up once at module level
let wsSubscribed = false

function setupWsSubscription() {
  if (wsSubscribed) return
  wsSubscribed = true

  // Import dynamically to avoid circular dependency issues
  // useNotifications has onMounted which can't be called outside component context
  import('./useNotifications').then(({ useNotifications }) => {
    const notifications = useNotifications()
    notifications.subscribeToLookups((event: LookupsInvalidateEvent) => {
      console.log('[useLookups] Received invalidation:', event.type, event.action)
      if (event.type === 'projects') {
        debouncedLoadProjects()
      } else if (event.type === 'people') {
        debouncedLoadPeople()
      } else if (event.type === 'tags') {
        debouncedLoadTags()
      }
    })
  })
}

/**
 * Shared composable for lookup data used in selectors.
 * All components share the same reactive data.
 * Automatically refreshes via WebSocket lookups:invalidate events.
 */
export function useLookups() {
  onMounted(() => {
    // Lazy load — only fetch if not loaded yet
    if (!projectsLoaded.value && !projectsLoading.value) {
      loadProjects()
    }
    if (!peopleLoaded.value && !peopleLoading.value) {
      loadPeople()
    }
    if (!tagsLoaded.value && !tagsLoading.value) {
      loadTags()
    }

    // Subscribe to WS invalidation
    setupWsSubscription()
  })

  return {
    projects,
    people,
    tags,
    tagGroups,
    tagColorMap,
    projectsLoading,
    peopleLoading,
    tagsLoading,
    reloadProjects: loadProjects,
    reloadPeople: loadPeople,
    reloadTags: loadTags,
    getTagColor,
    getContrastColor,
  }
}

/**
 * Get tag color by name from the shared tag registry.
 * Falls back to a deterministic hash-based color if tag is not in registry.
 */
function getTagColor(tagName: string): string {
  const color = tagColorMap.value.get(tagName.toLowerCase())
  if (color) return color
  // Deterministic fallback based on tag name (same algorithm as backend)
  const colors = [
    '#F44336', '#E91E63', '#9C27B0', '#673AB7',
    '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
    '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
    '#FFC107', '#FF9800', '#FF5722', '#795548', '#607D8B',
  ]
  let hash = 0
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Get contrast text color (black or white) for a given HEX background.
 */
function getContrastColor(hex: string): string {
  const color = hex.replace('#', '')
  const r = parseInt(color.substr(0, 2), 16)
  const g = parseInt(color.substr(2, 2), 16)
  const b = parseInt(color.substr(4, 2), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#FFFFFF'
}
